import { useSyncExternalStore } from "react";

// ONE fact, held in ONE place: "a material is open across the whole screen".
//
// Pavel, F97 (2026-08-07): "Kdyz mam otevreny nejaky dokument, jako v tomto
// pripade mam otevrenou prodejni stranku, tak by se mi tam nemely zobrazovat
// notifikace." A toast that lands over an open sales page covers the document
// AND the comments panel - the two things the client came to work with.
//
// Every full-screen reading surface in the app is a <DocOverlay>: the four
// client materials (navrh prodejni strategie, prodejni stranka, e-mail
// marketing, struktura webinare), the avatar viewer, and the partner's review
// room. So the OVERLAY registers itself while it is mounted and no surface has
// to keep its own copy of the condition. Two copies drift, and the law would
// then hold on one screen and silently fail on another - which is exactly the
// class of bug this module exists to prevent.
//
// Consumers: `ToastLayer` (src/toast-layer.tsx) is the only one today, and it
// gates EVERY floating message by default, so a future toast source inherits
// the law instead of having to remember it.

let openCount = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Call while a full-screen material is mounted; the returned function releases
 * it. A counter (not a boolean) because two overlays can legitimately overlap
 * for a frame while one closes and another opens, and StrictMode mounts every
 * effect twice. */
export function registerOpenDocument(): () => void {
  openCount += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openCount = Math.max(0, openCount - 1);
    emit();
  };
}

export function isDocumentOverlayOpen(): boolean {
  return openCount > 0;
}

function subscribeToDocumentOverlay(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useDocumentOverlayOpen(): boolean {
  // Both snapshots read the same counter. Outside a browser the counter is
  // always 0 (the overlay registers from an effect, and effects do not run
  // while rendering to a string), so this is the same answer a hardcoded
  // `false` would give - with the difference that a test can open a document
  // and observe the gate instead of having to trust it.
  return useSyncExternalStore(
    subscribeToDocumentOverlay,
    isDocumentOverlayOpen,
    isDocumentOverlayOpen
  );
}

/** Tests only: a leaked registration from one case would silence the toast in
 * every case after it. */
export function resetDocumentOverlayState(): void {
  openCount = 0;
  listeners.clear();
}
